<div class="p-5 mx-auto">
  <!-- Header -->
  <div class="flex justify-between items-center mb-5">
    <h1 class="text-2xl font-medium">{{ 'email-logs.list.title' | translate }}</h1>
  </div>

  <!-- Search + column picker -->
  <div class="flex gap-3 mb-4 items-center">
    <mat-form-field class="flex-1" subscriptSizing="dynamic">
      <mat-label>{{ 'email-logs.list.search-placeholder' | translate }}</mat-label>
      <input #searchBox matInput [value]="searchInput()" (input)="onSearchInput(searchBox.value)" />
      <mat-icon matSuffix>search</mat-icon>
    </mat-form-field>

    <button
      mat-icon-button
      [disabled]="!hasActiveFilters()"
      (click)="clearAllFilters()"
      matTooltip="{{ 'email-logs.filter.clear-all' | translate }}"
      [attr.aria-label]="'email-logs.filter.clear-all' | translate">
      <mat-icon>filter_alt_off</mat-icon>
    </button>

    <button
      mat-icon-button
      [matMenuTriggerFor]="columnMenu"
      matTooltip="{{ 'email-logs.list.column-picker' | translate }}"
      [attr.aria-label]="'email-logs.list.column-picker' | translate">
      <mat-icon>view_column</mat-icon>
    </button>
  </div>

  <mat-card>
    <mat-card-content class="!p-0">
      <div class="overflow-x-auto">
        <table mat-table [dataSource]="emailLogs()" class="w-full">

          <ng-container matColumnDef="subject">
            <th mat-header-cell *matHeaderCellDef>{{ 'email-logs.field.subject' | translate }}</th>
            <td mat-cell *matCellDef="let e" class="font-medium">{{ e.subject }}</td>
          </ng-container>

          <ng-container matColumnDef="logType">
            <th mat-header-cell *matHeaderCellDef>{{ 'email-logs.field.logType' | translate }}</th>
            <td mat-cell *matCellDef="let e" class="whitespace-nowrap">
              <span class="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100">
                {{ logTypeLabel(e.logType) }}
              </span>
            </td>
          </ng-container>

          <!--
            Coalesced state column: one chip showing `deliveryStatus ?? status`, with both axes in the
            tooltip. Problem states get an error hint (the row DTO carries no lastError — that lives on the
            inspect view) and retried rows a replay hint.
          -->
          <ng-container matColumnDef="state">
            <th mat-header-cell *matHeaderCellDef>{{ 'email-logs.field.state' | translate }}</th>
            <td mat-cell *matCellDef="let e" class="whitespace-nowrap">
              <span class="px-2 py-0.5 rounded text-xs font-medium" [class]="stateClass(e)" [matTooltip]="stateTooltip(e)">
                {{ stateLabel(e) }}
              </span>
              @if (isProblemRow(e)) {
                <mat-icon
                  class="!text-base !w-4 !h-4 align-middle ml-1 text-red-600 dark:text-red-400"
                  matTooltip="{{ 'email-logs.list.problem-hint' | translate }}"
                  [attr.aria-label]="'email-logs.list.problem-hint' | translate">
                  error_outline
                </mat-icon>
              }
              @if (e.attemptCount > 1) {
                <mat-icon
                  class="!text-base !w-4 !h-4 align-middle ml-1 text-gray-500 dark:text-gray-400"
                  matTooltip="{{ 'email-logs.list.retried' | translate: { count: e.attemptCount } }}"
                  [attr.aria-label]="'email-logs.list.retried' | translate: { count: e.attemptCount }">
                  replay
                </mat-icon>
              }
            </td>
          </ng-container>

          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef>{{ 'email-logs.field.pipelineStatus' | translate }}</th>
            <td mat-cell *matCellDef="let e" class="whitespace-nowrap">{{ statusLabel(e.status) }}</td>
          </ng-container>

          <ng-container matColumnDef="deliveryStatus">
            <th mat-header-cell *matHeaderCellDef>{{ 'email-logs.field.deliveryStatus' | translate }}</th>
            <td mat-cell *matCellDef="let e" class="whitespace-nowrap">{{ deliveryStatusLabel(e.deliveryStatus) }}</td>
          </ng-container>

          <!-- Recipient snapshot the email was queued for — also what a resend targets. -->
          <ng-container matColumnDef="recipient">
            <th mat-header-cell *matHeaderCellDef>{{ 'email-logs.field.recipient' | translate }}</th>
            <td mat-cell *matCellDef="let e" class="whitespace-nowrap">
              @if (e.toName) {
                <span>{{ e.toName }}</span>
                <span class="block text-xs text-gray-500 dark:text-gray-400">{{ e.toEmail }}</span>
              } @else {
                {{ e.toEmail }}
              }
            </td>
          </ng-container>

          <ng-container matColumnDef="customerName">
            <th mat-header-cell *matHeaderCellDef>{{ 'email-logs.field.customer' | translate }}</th>
            <td mat-cell *matCellDef="let e">{{ e.customerName ?? '—' }}</td>
          </ng-container>

          <ng-container matColumnDef="customerId">
            <th mat-header-cell *matHeaderCellDef>{{ 'email-logs.field.customerId' | translate }}</th>
            <td mat-cell *matCellDef="let e" class="whitespace-nowrap">{{ e.customerId ?? '—' }}</td>
          </ng-container>

          <ng-container matColumnDef="reservationNumber">
            <th mat-header-cell *matHeaderCellDef>{{ 'email-logs.field.reservation' | translate }}</th>
            <td mat-cell *matCellDef="let e" class="whitespace-nowrap">{{ e.reservationNumber ?? '—' }}</td>
          </ng-container>

          <ng-container matColumnDef="reservationId">
            <th mat-header-cell *matHeaderCellDef>{{ 'email-logs.field.reservationId' | translate }}</th>
            <td mat-cell *matCellDef="let e" class="whitespace-nowrap">{{ e.reservationId ?? '—' }}</td>
          </ng-container>

          <ng-container matColumnDef="attemptCount">
            <th mat-header-cell *matHeaderCellDef>{{ 'email-logs.field.attemptCount' | translate }}</th>
            <td mat-cell *matCellDef="let e" class="whitespace-nowrap">{{ e.attemptCount }}</td>
          </ng-container>

          <ng-container matColumnDef="publicId">
            <th mat-header-cell *matHeaderCellDef>{{ 'email-logs.field.publicId' | translate }}</th>
            <td mat-cell *matCellDef="let e" class="whitespace-nowrap font-mono text-xs" [matTooltip]="e.publicId">
              {{ e.publicId.slice(0, 8) }}
            </td>
          </ng-container>

          <!-- Queued moment. The real "sent on" is `acceptedAt`. -->
          <ng-container matColumnDef="createdOn">
            <th mat-header-cell *matHeaderCellDef>{{ 'email-logs.field.createdOn' | translate }}</th>
            <td mat-cell *matCellDef="let e" class="whitespace-nowrap">{{ e.createdOn | date: 'short' }}</td>
          </ng-container>

          <ng-container matColumnDef="acceptedAt">
            <th mat-header-cell *matHeaderCellDef>{{ 'email-logs.field.acceptedAt' | translate }}</th>
            <td mat-cell *matCellDef="let e" class="whitespace-nowrap">
              {{ e.acceptedAt ? (e.acceptedAt | date: 'short') : '—' }}
            </td>
          </ng-container>

          <!-- Filter header columns (generated from column definitions) -->
          @for (col of allColumns; track col.key) {
            <ng-container [matColumnDef]="'filter-' + col.key">
              <th mat-header-cell *matHeaderCellDef class="!p-1">
                @switch (col.filterType) {
                  @case ('text') {
                    <mat-form-field subscriptSizing="dynamic" class="w-full">
                      <input #filterBox matInput
                        placeholder="{{ (col.filterPlaceholderKey ?? 'email-logs.filter.placeholder') | translate }}"
                        [value]="getTextFilterValue(col.filterField!)"
                        (input)="onTextFilterInput(col.filterField!, filterBox.value)" />
                    </mat-form-field>
                  }
                  @case ('number') {
                    <mat-form-field subscriptSizing="dynamic" class="w-full">
                      <input #numBox matInput type="number" min="1"
                        placeholder="{{ (col.filterPlaceholderKey ?? 'email-logs.filter.placeholder') | translate }}"
                        [value]="getTextFilterValue(col.filterField!)"
                        (input)="onTextFilterInput(col.filterField!, numBox.value)" />
                    </mat-form-field>
                  }
                  @case ('dropdown') {
                    <mat-form-field subscriptSizing="dynamic" class="w-full">
                      <mat-select
                        [value]="getDropdownFilterValue(col.filterField!)"
                        (selectionChange)="onDropdownFilterChange(col.filterField!, $event.value)">
                        <mat-option value="">{{ 'email-logs.filter.all' | translate }}</mat-option>
                        @for (opt of dropdownOptions[col.dropdownSource!]; track opt) {
                          <mat-option [value]="opt">{{ optionLabel(col.dropdownSource!, opt) }}</mat-option>
                        }
                      </mat-select>
                    </mat-form-field>
                  }
                  @case ('state') {
                    <!-- One dropdown over both state axes; the component routes the value to the right API param. -->
                    <mat-form-field subscriptSizing="dynamic" class="w-full">
                      <mat-select
                        [value]="getStateFilterValue()"
                        (selectionChange)="onStateFilterChange($event.value)">
                        <mat-option value="">{{ 'email-logs.filter.all' | translate }}</mat-option>
                        @for (opt of stateFilterOptions; track opt) {
                          <mat-option [value]="opt">{{ stateOptionLabel(opt) }}</mat-option>
                        }
                      </mat-select>
                    </mat-form-field>
                  }
                  @case ('daterange') {
                    <mat-form-field subscriptSizing="dynamic" class="w-full min-w-48">
                      <mat-date-range-input [rangePicker]="rangePicker">
                        <input matStartDate
                          [placeholder]="'email-logs.filter.from' | translate"
                          [value]="getDateFilterValue(col.filterFieldFrom!)"
                          (dateChange)="onDateFilterChange(col.filterFieldFrom!, $event.value)" />
                        <input matEndDate
                          [placeholder]="'email-logs.filter.to' | translate"
                          [value]="getDateFilterValue(col.filterFieldTo!)"
                          (dateChange)="onDateFilterChange(col.filterFieldTo!, $event.value)" />
                      </mat-date-range-input>
                      @if (getDateFilterValue(col.filterFieldFrom!) || getDateFilterValue(col.filterFieldTo!)) {
                        <button matIconSuffix mat-icon-button type="button"
                          (click)="clearDateRange(col)"
                          matTooltip="{{ 'email-logs.filter.clear' | translate }}"
                          [attr.aria-label]="'email-logs.filter.clear' | translate">
                          <mat-icon>close</mat-icon>
                        </button>
                      } @else {
                        <mat-datepicker-toggle matIconSuffix [for]="rangePicker" />
                      }
                      <mat-date-range-picker #rangePicker />
                    </mat-form-field>
                  }
                }
              </th>
            </ng-container>
          }

          <!-- No-data row -->
          <tr *matNoDataRow>
            @if (!emailLogsResource.isLoading()) {
              <td [attr.colspan]="displayedColumns().length" class="p-0">
                <div class="text-center p-10">
                  <mat-icon class="!text-6xl !w-16 !h-16 !text-gray-400 mb-4">mail_outline</mat-icon>
                  <p class="text-gray-700 dark:text-gray-300 text-base">{{ 'email-logs.list.no-emails' | translate }}</p>
                </div>
              </td>
            }
          </tr>

          <!-- Row definitions -->
          <tr mat-header-row *matHeaderRowDef="displayedColumns()"></tr>
          <tr mat-header-row *matHeaderRowDef="filterColumns()" class="filter-row"></tr>
          <tr
            mat-row
            *matRowDef="let row; columns: displayedColumns()"
            (click)="navigateToEmailLog(row.id)"
            class="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"></tr>
        </table>
      </div>

      <!--
        Keyset paging footer. The API hands out an opaque `nextCursor` and cannot jump to page N, so there
        is no mat-paginator here: only next/previous (walked cursor stack) plus the page size. `totalCount`
        still describes the whole filtered set, which is what the row range is measured against.
      -->
      @if (totalCount() > 0) {
        <div class="flex items-center justify-end gap-4 px-4 py-2 border-t border-gray-200 dark:border-gray-700">
          <span class="text-sm text-gray-600 dark:text-gray-400">
            {{ 'email-logs.list.range' | translate: { start: rangeStart(), end: rangeEnd(), total: totalCount() } }}
          </span>

          <mat-form-field subscriptSizing="dynamic" class="w-24">
            <mat-label>{{ 'email-logs.list.page-size' | translate }}</mat-label>
            <mat-select [value]="pageSize()" (selectionChange)="onPageSizeChange($event.value)">
              @for (size of pageSizeOptions; track size) {
                <mat-option [value]="size">{{ size }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <div class="flex items-center">
            <button
              mat-icon-button
              [disabled]="!canGoPrevious()"
              (click)="goToPreviousPage()"
              matTooltip="{{ 'email-logs.list.previous-page' | translate }}"
              [attr.aria-label]="'email-logs.list.previous-page' | translate">
              <mat-icon>chevron_left</mat-icon>
            </button>
            <button
              mat-icon-button
              [disabled]="!canGoNext()"
              (click)="goToNextPage()"
              matTooltip="{{ 'email-logs.list.next-page' | translate }}"
              [attr.aria-label]="'email-logs.list.next-page' | translate">
              <mat-icon>chevron_right</mat-icon>
            </button>
          </div>
        </div>
      }
    </mat-card-content>
  </mat-card>
</div>

<!-- Column picker menu -->
<mat-menu #columnMenu="matMenu">
  <div class="px-2 py-1 min-w-48" (click)="$event.stopPropagation()">
    <p class="text-xs font-medium text-gray-500 uppercase tracking-wider px-2 pb-2">
      {{ 'email-logs.list.column-picker' | translate }}
    </p>
    @for (col of allColumns; track col.key) {
      <div class="flex items-center px-2 py-1">
        <mat-checkbox [checked]="isColumnVisible(col.key)" (change)="toggleColumn(col.key)">
          {{ col.labelKey | translate }}
        </mat-checkbox>
      </div>
    }
  </div>
</mat-menu>
